// ============================================================
// CLUBHUB – Rechnungsliste: die Ansicht in der Adresse
// ============================================================
// Wie im Agent (`rechnungen/view.ts`): Filter, Suche und Sortierung
// stehen in den searchParams, der Filterkopf schreibt sie, die
// Seite liest sie. Ohne Angabe zeigt die Liste die OFFENEN
// Rechnungen — eine Rechnungsliste öffnet man, um zu sehen, was
// noch hereinkommen muss. Bezahltes und Storniertes ist Archiv.
// ============================================================
import type { InvoiceKind, InvoiceStatus } from "@/generated/prisma/enums";
import { readSort, type SortColumns, type SortState } from "@/lib/sorting";
import { mahnStand, type Mahnregeln } from "./mahnwesen";
import type { RechnungView } from "./views";

export const STATUS_REIHE: InvoiceStatus[] = ["DRAFT", "OPEN", "PARTIALLY_PAID", "OVERDUE", "PAID", "CANCELLED"];
export const OFFEN_STATUS: InvoiceStatus[] = ["OPEN", "PARTIALLY_PAID", "OVERDUE"];
export const ARTEN: InvoiceKind[] = ["FEE", "GENERAL", "CANCELLATION", "CREDIT_NOTE"];

export const SORT_COLUMNS = {
  nummer: { label: "Nummer", first: "ab" },
  empfaenger: { label: "Empfänger", first: "auf" },
  datum: { label: "Datum", first: "ab" },
  faellig: { label: "Fälligkeit", first: "ab" },
  betrag: { label: "Betrag", first: "ab" },
  offen: { label: "Offen", first: "ab" },
  status: { label: "Status", first: "auf" },
} as const satisfies SortColumns<string>;
export type SortKey = keyof typeof SORT_COLUMNS;
export const DEFAULT_SORT: SortKey = "nummer";

export type Ansicht = {
  q: string;
  /** Durchgelassene Status — leer heißt keiner. */
  status: InvoiceStatus[];
  statusVorgabe: boolean;
  art: InvoiceKind | "";
  /** „2026“, „2026-q3“ oder leer. */
  zeit: string;
  /** Nur Rechnungen, deren nächste Mahnstufe fällig ist. */
  mahnung: boolean;
  lauf: string;
  kontakt: string;
  sort: SortState<SortKey>;
};

export const ALLE_PARAMS = ["q", "status", "art", "zeit", "mahnung", "lauf", "kontakt", "sortierung", "richtung", "umfang"] as const;

const IST_STATUS = new Set<string>(STATUS_REIHE);

export function ansichtLesen(p: Record<string, string | string[] | undefined>): Ansicht {
  const eins = (name: string) => {
    const w = p[name];
    return ((Array.isArray(w) ? w[0] : w) ?? "").trim();
  };
  const roh = eins("status");
  const gewaehlt = roh
    .split(",")
    .map((s) => s.trim().toUpperCase())
    .filter((s): s is InvoiceStatus => IST_STATUS.has(s));
  // Nichts gesagt (oder „offen“ von der Übersicht) heißt: die Vorgabe.
  const vorgabe = roh === "" || roh === "offen" || (!["alle", "keine"].includes(roh) && gewaehlt.length === 0);
  const status = vorgabe ? [...OFFEN_STATUS] : roh === "alle" ? [...STATUS_REIHE] : roh === "keine" ? [] : gewaehlt;
  const art = eins("art").toUpperCase();
  return {
    q: eins("q"),
    status,
    statusVorgabe: vorgabe,
    art: (ARTEN as string[]).includes(art) ? (art as InvoiceKind) : "",
    zeit: /^\d{4}(-q[1-4])?$/.test(eins("zeit")) ? eins("zeit") : "",
    mahnung: eins("mahnung") === "faellig",
    lauf: eins("lauf"),
    kontakt: eins("kontakt"),
    sort: readSort(p, SORT_COLUMNS, DEFAULT_SORT),
  };
}

/** Der Wert, der für eine Statusauswahl in die Adresse gehört (`null` = Vorgabe). */
export function statusParam(liste: InvoiceStatus[]): string | null {
  if (liste.length === 0) return "keine";
  if (liste.length === STATUS_REIHE.length) return "alle";
  if (liste.length === OFFEN_STATUS.length && OFFEN_STATUS.every((s) => liste.includes(s))) return null;
  return STATUS_REIHE.filter((s) => liste.includes(s)).join(",");
}

export function umschalten<T>(liste: readonly T[], wert: T): T[] {
  return liste.includes(wert) ? liste.filter((x) => x !== wert) : [...liste, wert];
}

export function aktiveFilter(a: Ansicht): number {
  return [a.q, !a.statusVorgabe, a.art, a.zeit, a.mahnung, a.lauf, a.kontakt].filter(Boolean).length;
}

/** Liegt das Datum im gewählten Zeitraum? */
export function imZeitraum(iso: string, zeit: string): boolean {
  if (!zeit) return true;
  const [jahr, q] = zeit.split("-q");
  if (iso.slice(0, 4) !== jahr) return false;
  if (!q) return true;
  const monat = Number(iso.slice(5, 7));
  return Math.ceil(monat / 3) === Number(q);
}

export function zeitraumLabel(zeit: string): string {
  if (!zeit) return "";
  const [jahr, q] = zeit.split("-q");
  return q ? `${q}. Quartal ${jahr}` : jahr!;
}

type Dimension = "status" | "art" | "zeit" | "mahnung" | "suche";

/** Passt die Zeile — bis auf die genannte Dimension (damit die Zähler im Kopf mitzählen)? */
export function passt(r: RechnungView, a: Ansicht, heute: string, ausser?: Dimension, regeln?: Mahnregeln): boolean {
  if (ausser !== "status" && !a.status.includes(r.status)) return false;
  if (ausser !== "art" && a.art && r.kind !== a.art) return false;
  if (ausser !== "zeit" && !imZeitraum(r.issueDate, a.zeit)) return false;
  if (ausser !== "mahnung" && a.mahnung && mahnStand(r, heute, regeln)?.lage !== "faellig") return false;
  if (a.lauf && r.billingRunId !== a.lauf) return false;
  if (a.kontakt && r.contactId !== a.kontakt) return false;
  if (ausser !== "suche" && a.q) {
    const n = a.q.toLowerCase();
    const heu = [r.number, r.subject, r.recipient.name, r.recipient.extra, r.recipient.memberNumber, r.mandateReference]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();
    if (!heu.includes(n)) return false;
  }
  return true;
}
